// @flow
import classnames from 'classnames';
import React from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';

import { withTranslation } from 'react-i18next';

import { CircularProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import WifiTetheringOutlinedIcon from '@material-ui/icons/WifiTetheringOutlined';
import LoadingText from '../../components/LoadingText';
import ScribeTranscriptContainer from './ScribeTranscriptContainer';
import ScribeScrollWithTranscriptContainer from './ScribeScrollWithTranscriptContainer';
import ScribeCreateTranscriptContainer from './ScribeCreateTranscriptContainer';
import FinalTranscriptListContainer from './FinalTranscriptListContainer';
import { getOS } from '../../utils';
import { getScribes, hasScribe } from '../../utils/status';
import { ScribeCorrectionModeAccurate } from '../../types/config';
import type { ScribeCorrectionMode } from '../../types/config';

const styles = {
  loadingText: {
    fontSize: 40,
    userSelect: 'none',
    '&:after': {
      fontSize: 'unset',
      height: 'unset',
      marginTop: 'unset',
    },
  },
};

type State = {
  showLoadingCircle: boolean,
};

type Props = {
  classes: Object,
  correctionMode: ScribeCorrectionMode,
  currentPlatform: string,
  features: FeatureMap,
  fontSize: number,
  electronCaptionMode: boolean,
  furthestObservedCursors: { [participantHash: string]: { tid: string, index: number } },
  inConferenceCall: boolean,
  lang: string,
  loaded: boolean,
  scribe: string,
  speakers: Object,
  status: Object,
  theme: Object,
  t: Function,
  transcripts: TranscriptMap,
  transcriptsCurrent: Array<string>,
  transcriptsFinal: Array<string>,
  user: Object,
  recording: Boolean,
  audioParamsSent: Boolean,
};

// Maybe have a finalized TranscriptsList component that only rerenders when
class ScribeTranscriptList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showLoadingCircle: false,
    };
  }

  componentDidUpdate() {
    const { transcriptsCurrent, electronCaptionMode, recording, audioParamsSent } = this.props;

    const audioRecorder = document.querySelector('.audio-indicator');
    if (audioRecorder) {
      if (transcriptsCurrent && transcriptsCurrent.length > 0) {
        audioRecorder.classList.remove('animate');
      } else if (
        transcriptsCurrent &&
        transcriptsCurrent.length === 0 &&
        electronCaptionMode &&
        recording &&
        audioParamsSent
      ) {
        audioRecorder.classList.add('animate');
      }

      if (!recording || !audioParamsSent || !electronCaptionMode) {
        audioRecorder.classList.remove('animate');
      }
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const { loaded, status } = this.props;
    const { twilioCalls: nextTwilioCalls } = nextProps.status;
    if (!nextProps.loaded && !loaded) {
      return false;
    }

    if (Object.keys(nextTwilioCalls || {}).length === 1 && Object.keys(status.twilioCalls || {}).length === 0) {
      const conferenceType = nextTwilioCalls[Object.keys(nextTwilioCalls)[0]];
      this.setState({
        showLoadingCircle: true,
      });
      if (conferenceType === 'zoom') {
        setTimeout(() => {
          this.setState({
            showLoadingCircle: false,
          });
        }, 10000);
      } else {
        setTimeout(() => {
          this.setState({
            showLoadingCircle: false,
          });
        }, 10000);
      }
    }
    return true;
  }

  render = () => {
    const {
      classes,
      correctionMode,
      currentPlatform,
      features,
      fontSize,
      electronCaptionMode,
      furthestObservedCursors,
      inConferenceCall,
      lang,
      scribe,
      speakers,
      status,
      t,
      theme,
      transcripts,
      transcriptsCurrent,
      transcriptsFinal,
      user,
      recording,
      audioParamsSent,
    } = this.props;

    const { showLoadingCircle } = this.state;
    let showFirstAuthor = false;
    let lastAuthorTrack = null;
    transcriptsCurrent.forEach((tid, i) => {
      const transcript = transcripts[tid];
      const authorTrack = `${transcript.author}-${transcript.trackName}`;
      if (i > 0 && authorTrack !== lastAuthorTrack) {
        showFirstAuthor = true;
      }
      lastAuthorTrack = authorTrack;
    });

    let lt = {};
    let isDisplayingEllipsis = false;

    const showListeningNow =
      electronCaptionMode && recording && audioParamsSent && (!transcriptsFinal || transcriptsFinal.length < 1);

    let furthestObservedByScribe;
    if (hasScribe(status)) {
      // Finding the furthest word any scribe has clicked on
      const scribeCursors = getScribes(status).map(scribe => furthestObservedCursors[scribe.hash]);
      for (const cursor of scribeCursors) {
        if (!cursor) continue;
        if (
          !furthestObservedByScribe ||
          furthestObservedByScribe.tid < cursor.tid ||
          (furthestObservedByScribe.tid === cursor.tid && furthestObservedByScribe.index < cursor.index)
        ) {
          furthestObservedByScribe = cursor;
        }
      }
    }

    return (
      <ScribeScrollWithTranscriptContainer>
        {transcriptsFinal.length !== 0 && (
          <div
            style={{
              paddingTop: 24,
              paddingLeft: window.isElectron && electronCaptionMode && !inConferenceCall ? 0 : 20,
              paddingRight: window.isElectron && electronCaptionMode ? 0 : 20,
              paddingBottom: 12,
              position: 'relative',
              backgroundColor: theme.palette.background[theme.palette.type],
            }}
          >
            <div style={{ margin: '0 auto' }}>
              <FinalTranscriptListContainer
                features={features}
                lang={lang}
                transcriptsFinal={transcriptsFinal}
                user={user}
                fontSize={fontSize}
                currentPlatform={currentPlatform}
                speakers={speakers}
                theme={theme}
              />
            </div>
          </div>
        )}
        {inConferenceCall && showLoadingCircle && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress
              color="secondary"
              style={{
                marginBottom: '12px',
              }}
            />
            <Typography>{t('conversation.call.joiningConferenceCall')}</Typography>
          </div>
        )}
        {transcriptsCurrent && transcriptsCurrent.length > 0 ? (
          <div
            style={{
              background: theme.palette.background,
              paddingLeft: window.isElectron && electronCaptionMode && !inConferenceCall ? 0 : 20,
              paddingRight: window.isElectron && electronCaptionMode ? 0 : 20,
            }}
          >
            <div
              style={{
                margin: '0 auto',
                scrollBehavior: 'smooth',
                height: '100%',
                paddingTop: electronCaptionMode ? 24 : 0,
                paddingBottom: electronCaptionMode ? 24 : 40,
                WebkitAppRegion: getOS() !== 'Mac' ? 'no-drag' : undefined,
              }}
            >
              {transcriptsCurrent.map((tid, i) => {
                const transcript = transcripts[tid];
                let furthestScribeIndex;
                if (
                  correctionMode === ScribeCorrectionModeAccurate &&
                  furthestObservedByScribe &&
                  tid === furthestObservedByScribe.tid &&
                  Date.now() - transcript.timestampMs < 120000
                ) {
                  furthestScribeIndex = furthestObservedByScribe.index;
                  // We're displaying the inline ellipsis, so we're hiding
                  // the per-line ellipsis
                  isDisplayingEllipsis = true;
                }

                if (
                  correctionMode === ScribeCorrectionModeAccurate &&
                  !scribe &&
                  furthestObservedByScribe &&
                  tid > furthestObservedByScribe.tid &&
                  Date.now() - transcript.timestampMs < 120000
                ) {
                  // If there are multiple lines the scribe needs to see, this
                  // make sure only one pair of ellipsis is shown.
                  if (isDisplayingEllipsis) return null;

                  // Don't show the transcript at all if:
                  //  * scribeCorrectionMode has been set to Accurate AND
                  //  * the current user is not currently scribing AND
                  //  * we know how far the scribes have looked AND
                  //  * they haven't touched the transcript yet AND
                  //  * the transcript is relatively fresh (we always show old transcripts)
                  isDisplayingEllipsis = true;
                  return (
                    <div key={`loading-${tid}`} style={{ fontSize: 32, height: '1em' }}>
                      <LoadingText />
                    </div>
                  );
                }

                const component = (
                  <ScribeTranscriptContainer
                    id={tid}
                    key={tid}
                    dividerHeight={
                      transcripts[tid].authorShow && i !== 0
                        ? Math.floor(fontSize * 1.2)
                        : transcripts[tid].author !== lt.author && i === 0 && !electronCaptionMode
                        ? Math.floor(fontSize * 0.5)
                        : 0
                    }
                    hideFirstInternal={!transcriptsFinal?.length && i === 0 && !showFirstAuthor}
                    furthestScribeIndex={furthestScribeIndex}
                  />
                );

                if (transcripts[tid].speechLang && transcripts[tid].texts[transcripts[tid].speechLang.split('-')[0]]) {
                  lt = transcripts[tid];
                }

                return component;
              })}
            </div>
          </div>
        ) : (
          <>
            {showListeningNow && (
              <div
                style={{
                  background: theme.palette.background,
                  paddingTop: 24,
                  paddingLeft: window.isElectron && electronCaptionMode && !inConferenceCall ? 0 : 20,
                  paddingRight: window.isElectron && electronCaptionMode ? 0 : 20,
                  paddingBottom: 24,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <div
                  style={{
                    marginLeft: window.isElectron && electronCaptionMode ? 0 : 40,
                    WebkitAppRegion: getOS() !== 'Mac' ? 'no-drag' : undefined,
                  }}
                >
                  <LoadingText className={classes.loadingText}>{t('captions.listeningNow')}</LoadingText>
                </div>
              </div>
            )}
          </>
        )}
      </ScribeScrollWithTranscriptContainer>
    );
  };
}

export default withTranslation()(withStyles(styles)(withTheme(ScribeTranscriptList)));
